import React, { useEffect, useRef, useState } from "react";

const DEFAULT_VIDEO =
  "https://customer-assets.emergentagent.com/job_grove-design/artifacts/zbo7arxw_kling_20260625_VIDEO_Lake_Como__5124_0.mp4";

const CinematicDivider = ({
  src = DEFAULT_VIDEO,
  caption = "Adorned across the world",
  subCaption = "Wherever the light is, she shines",
  height,
  testId = "cinematic-divider",
}) => {
  const videoRef = useRef(null);
  const sectionRef = useRef(null);
  const [active, setActive] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActive(true);
            io.disconnect();
          }
        });
      },
      { rootMargin: "200% 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!active) return undefined;
    const id = setTimeout(() => {
      videoRef.current?.play().catch(() => {});
    }, 50);
    return () => clearTimeout(id);
  }, [active]);

  return (
    <section
      ref={sectionRef}
      className="relative w-full overflow-hidden"
      style={{
        height: height || "clamp(280px, 38vw, 460px)",
        background: "#2B1A20",
      }}
      data-testid={testId}
    >
      <div
        className="absolute inset-0 transition-opacity duration-700"
        style={{
          opacity: loaded ? 0 : 1,
          background:
            "linear-gradient(135deg, #4A2C35 0%, #2B1A20 60%, #C5985A 100%)",
        }}
        aria-hidden
      />
      {active && (
        <video
          ref={videoRef}
          src={src}
          muted
          loop
          playsInline
          autoPlay
          preload="auto"
          onLoadedData={() => setLoaded(true)}
          onCanPlay={(e) => e.currentTarget.play().catch(() => {})}
          className="absolute inset-0 w-full h-full object-cover"
          aria-hidden
        />
      )}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, rgba(43,26,32,0.55) 0%, rgba(43,26,32,0) 35%, rgba(43,26,32,0) 65%, rgba(43,26,32,0.72) 100%)",
        }}
        aria-hidden
      />
      <div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center">
        <p className="text-[10px] lg:text-[11px] tracking-[0.36em] uppercase text-cream/85 mb-3">
          ✦ Far &amp; Sunkissed ✦
        </p>
        <p
          className="font-serif italic text-cream leading-tight"
          style={{
            fontSize: "clamp(28px, 6.4vw, 56px)",
            textShadow: "0 2px 18px rgba(0,0,0,0.55)",
          }}
        >
          {caption}
        </p>
        <p className="mt-2.5 text-xs lg:text-sm text-cream/85 tracking-wide max-w-md px-2">
          {subCaption}
        </p>
      </div>
      <span className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/40 to-transparent" aria-hidden />
      <span className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/40 to-transparent" aria-hidden />
    </section>
  );
};

export default CinematicDivider;
